import { Button } from '@florencelabs/button';
import { useRouter } from 'next/navigation';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDecisionTree,
  setSchedulingFlowType,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { getProviderPrimaryPhoneNumber } from '@provider-directory/functions/get-available-phone';
import { useAnalytics } from '../analytics/analytics-provider';
import {
  useOrgContext,
  useOrgContextBasedOnHospitalAssociations,
} from '../org-context/org-context-provider';

interface ErrorButtonProps {
  variant: 'systemOutline' | 'clientMain';
  isDisabled?: boolean;
}

const BackToSearchWithRFVButton = ({
  variant = 'systemOutline',
  isDisabled,
}: ErrorButtonProps): ReactElement => {
  const router = useRouter();
  const { provider } = useProvider();
  const { key } = useOrgContextBasedOnHospitalAssociations(
    provider?.hospitalAssociations,
  );
  const { t } = useTranslation(key);
  const { decisionTree } = useDecisionTree();
  const selectedDecisionTree = decisionTree.rfvId;

  return (
    <Button
      isDisabled={isDisabled}
      testId={'back-to-search-btn'}
      slot={'close'}
      variant={variant}
      className={'py-[10px]'}
      onPress={() => {
        setSchedulingFlowType('BookAnotherProvider', { sticky: true });
        router.push(
          `/providers/find?reason-for-visit=${selectedDecisionTree}`,
          {
            scroll: false,
          },
        );
      }}
    >
      {t('schedule.viewProviders')}
    </Button>
  );
};
const BackToSearchButton = ({
  variant = 'systemOutline',
}: ErrorButtonProps): ReactElement => {
  const { provider } = useProvider();
  const { key } = useOrgContextBasedOnHospitalAssociations(
    provider?.hospitalAssociations,
  );
  const { t } = useTranslation(key);

  return (
    <Button
      testId={'back-to-search-btn'}
      slot={'close'}
      variant={variant}
      className={'py-[10px]'}
    >
      {t('schedule.backToSearch')}
    </Button>
  );
};

const LoginToMyChartButton = ({
  variant = 'systemOutline',
}: ErrorButtonProps): ReactElement => {
  const { provider } = useProvider();
  // Only `key` is needed — the translation namespace of the association-resolved
  // directory. DIR-3583 moved the MyChart login URL off this sibling config.
  const { key } = useOrgContextBasedOnHospitalAssociations(
    provider?.hospitalAssociations,
  );
  const { t } = useTranslation(key);
  const analytics = useAnalytics();
  const orgContext = useOrgContext();
  // Resolved from the ACTIVE directory (not the association-resolved sibling
  // above), consistent with how the MyChart scheduling URL is resolved.
  const myChartLoginUrl = orgContext.getMyChartLoginUrl(provider ?? undefined);

  return (
    <Button
      testId={'login-mychart-btn'}
      variant={variant}
      onPress={() => {
        // MyChart is not configured for this directory — no-op rather than
        // opening `about:blank`.
        if (!myChartLoginUrl) {
          return;
        }
        analytics.ehrRedirectEvent({
          linkContextPage: 'ProviderDetails',
          linkContextComponent: 'DecisionTree',
          provider: provider ?? undefined,
          phoneNumber: getProviderPrimaryPhoneNumber(
            provider,
            orgContext.getAssociationPhoneSelection(),
          ),
          schedulingFlowType: getSchedulingFlowType(),
          redirectUrl: myChartLoginUrl,
        });
        window.open(myChartLoginUrl, '_blank');
      }}
      className={'py-[10px]'}
    >
      {t('schedule.logIntoMyChart')}
    </Button>
  );
};

export type DecisionTreeErrorButtonType =
  | 'login-to-mychart'
  | 'back-to-search'
  | 'view-providers'
  | 'back-to-search-with-rfv';
export interface DecisionTreeErrorButtonProps extends ErrorButtonProps {
  type: DecisionTreeErrorButtonType;
}

export const DecisionTreeErrorButton = (
  props: DecisionTreeErrorButtonProps,
): ReactElement => {
  const { type, ...restProps } = props;
  switch (type) {
    case 'login-to-mychart':
      return <LoginToMyChartButton {...restProps} />;
    case 'back-to-search':
      return <BackToSearchButton {...restProps} />;
    case 'back-to-search-with-rfv':
      return <BackToSearchWithRFVButton {...restProps} />;
    default:
      return <BackToSearchButton {...restProps} />;
  }
};
